import { Stack } from "@fluentui/react";
import {
  stackTokens,
  styles,
} from "../../ExperimentDetails/ExperimentDetailsForm/experimentDetailsForm.styles";
import { styles as extensionDetailsStyles } from "../extensionDetails.styles";
import { BaseTextField, FormRow } from "../../ExperimentDetails/FormComponents";
import { ExtensionDetailsType } from "../../../Types";
import { useCallback, useMemo, useEffect } from "react";
import { isEmptyArray, isEmptyObject } from "../../../Shared/shared.utils";
import { FIELD_VALIDATORS } from "./extensionDetailsValidators";
import useAppInsights from "../../../Hooks/UseAppInsights";
import debounce from "lodash/debounce";
import BaseTagDropdownField from "../../ExperimentDetails/FormComponents/BaseTagDropdownField";
import { useFetchBaseJsonConfigAll } from "../../../Hooks/AdoHooks/UseFetchBaseJsonConfigAll";

interface ExtensionDetailsFormProps {
  /**
   * Form context containing form state, handlers, and validation logic
   */
  formState: ExtensionDetailsType;
  /**
   * Function to update form state in the parent component
   */
  setFormState: React.Dispatch<React.SetStateAction<ExtensionDetailsType>>;
  /**
   * Validation errors keyed by field name
   */
  errors: Record<string, string[]>;
  /**
   * Function to update form errors in the parent component, if needed for async validation or on submit validation
   */
  setErrors: (errors: Record<string, string[]>) => void;
  /**
   * When true, form fields are editable
   */
  isEditMode?: boolean;
}

/**
 * Form component with all the extension related details. These details are shared
 * between all experiments within an extension.
 */
const ExtensionDetailsForm: React.FC<ExtensionDetailsFormProps> = ({
  formState,
  setFormState,
  isEditMode = false,
  errors = {},
  setErrors,
}) => {
  const { trackOperationEvent } = useAppInsights("ExtensionDetailsForm");
  const { value: baseJsonFiles = [], loading } = useFetchBaseJsonConfigAll();
  const experimentOptions = Array.from(
    new Set(
      baseJsonFiles.map(({ extensionFilters }) => extensionFilters).flat(),
    ),
  ).filter((x): x is string => Boolean(x));

  useEffect(() => {
    // Set default values for assignmentUnit and environment if they are not already set
    // Will still allow users to save form if default values were not originally set
    if (!formState.assignmentUnit) {
      setFormState((prev: ExtensionDetailsType) => ({
        ...prev,
        assignmentUnit: "muid",
      }));
    }
    if (!formState.environment) {
      setFormState((prev: ExtensionDetailsType) => ({
        ...prev,
        environment: "Prod",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only update initial default values
  }, [formState.assignmentUnit, formState.environment]);

  const validateForm = useCallback(
    (updatedFormState: ExtensionDetailsType) => {
      const newErrors: Record<string, string[]> = {};
      const validationFields = Object.keys(
        FIELD_VALIDATORS,
      ) as (keyof ExtensionDetailsType)[];
      for (const key of validationFields) {
        const validator = FIELD_VALIDATORS[key];
        if (validator) {
          const fieldErrors = validator(
            updatedFormState[key],
            updatedFormState,
          );
          if (fieldErrors && fieldErrors.length > 0) {
            newErrors[key] = fieldErrors;
          }
        }
      }
      setErrors(newErrors);
    },
    [setErrors],
  );

  const debounceValidateAndTrack = useMemo(
    () =>
      debounce(
        (
          field: string,
          value: string | undefined,
          updated: ExtensionDetailsType,
        ) => {
          const isValid = validateForm(updated);
          trackOperationEvent({
            operation: "FormChange",
            function: "debounceValidateAndTrack",
            properties: {
              field,
              value,
              isValid,
            },
          });
        },
        500,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- allows debounce
    [],
  );

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      const key = field as keyof ExtensionDetailsType;
      let updated = { ...formState };
      // Remove field if value is empty
      if (
        value === null ||
        value === undefined ||
        value === "" ||
        isEmptyArray(value) ||
        isEmptyObject(value)
      ) {
        delete updated[key];
      } else {
        updated[key] = value;
      }
      setFormState(updated);
      debounceValidateAndTrack(field, value, updated);
    },
    [debounceValidateAndTrack, formState, setFormState],
  );

  return (
    <Stack
      className={extensionDetailsStyles.extensionDetailsForm}
      tokens={stackTokens}
    >
      {/* Extension Fields Accordion */}
      <Stack tokens={stackTokens}>
        {/* Row 1: Display Name, Secret Id */}
        <FormRow horizontal>
          <BaseTextField
            fieldKey="displayName"
            label="Display Name"
            value={formState.displayName}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="Used when displaying information about your Azure service. Recommended to match the name from the service tree"
            onChange={handleFieldChange}
            errorMessage={errors.displayName || undefined}
          />
          <BaseTextField
            fieldKey="secretId"
            label="Secret Id"
            value={formState.secretId}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="Secret ID of the SNI Certificate from onboarding setup. Can be found in the certificate details"
            onChange={handleFieldChange}
            errorMessage={errors.secretId || undefined}
          />
        </FormRow>
        {/* Assignment Unit */}
        <FormRow horizontal>
          <BaseTextField
            fieldKey="assignmentUnit"
            label="Assignment Unit"
            value={formState.assignmentUnit}
            isRequired={false}
            isEdit={isEditMode}
            subtitle="Assignment unit for the experiment. Defaults to 'muid'"
            onChange={handleFieldChange}
            errorMessage={errors.assignmentUnit || undefined}
            className={styles.experimentDetailsFormColStyle}
          />
          <BaseTextField
            fieldKey="environment"
            label="Environment"
            value={formState.environment}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="The environment name. Must be 'Prod' (national clouds not supported yet)"
            onChange={handleFieldChange}
            errorMessage={errors.environment || undefined}
            className={styles.experimentDetailsFormColStyle}
          />
        </FormRow>
        <FormRow horizontal>
          {/* Extension Filters */}
          <BaseTagDropdownField
            fieldKey="extensionFilters"
            label="Extension Filters"
            selectedOptions={formState.extensionFilters ?? []}
            options={experimentOptions}
            onChange={handleFieldChange}
            isEdit={isEditMode}
            subtitle="Array of extension names for audience filtering"
            placeholder="Add extension filter..."
            grow
            errorMessage={errors.extensionFilters || undefined}
            loading={loading}
          />
        </FormRow>
        {/* Service Identifier and Service Group */}
        <FormRow horizontal>
          <BaseTextField
            fieldKey="serviceIdentifier"
            label="Service Identifier"
            value={formState.serviceIdentifier}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="Unique identifier for your subscription, Must be registered with Ev2 using: Register-AzureServiceSubscription -ServiceIdentifier xxx -SubscriptionKey 'UniqueString'"
            onChange={handleFieldChange}
            errorMessage={errors.serviceIdentifier || undefined}
          />
          <BaseTextField
            fieldKey="serviceGroup"
            label="Service Group"
            value={formState.serviceGroup}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="Human-readable name of your Azure service used to differentiate services with the same service tree identifier. Recommended to match the name from the service tree"
            onChange={handleFieldChange}
            errorMessage={errors.serviceGroup || undefined}
          />
        </FormRow>

        {/* Tenant Id */}
        <FormRow horizontal>
          <BaseTextField
            fieldKey="tenantId"
            label="Tenant Id"
            value={formState.tenantId}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="Tenant ID for all your subscriptions and resources"
            onChange={handleFieldChange}
            errorMessage={errors.tenantId || undefined}
            grow
          />
        </FormRow>

        {/* Subscription Key */}
        <FormRow horizontal>
          <BaseTextField
            fieldKey="subscriptionKey"
            label="Subscription Key"
            value={formState.subscriptionKey}
            isRequired={true}
            isEdit={isEditMode}
            onChange={handleFieldChange}
            errorMessage={errors.subscriptionKey || undefined}
            grow
          />
        </FormRow>

        {/* Application Id */}
        <FormRow
          horizontal
          className={extensionDetailsStyles.extensionDetailsLastField}
        >
          <BaseTextField
            fieldKey="applicationId"
            label="Application Id"
            value={formState.applicationId}
            isRequired={true}
            isEdit={isEditMode}
            subtitle="Application ID of the Entra App Registration from onboarding setup. Can be found in the Azure Entra ID-App Registration"
            onChange={handleFieldChange}
            errorMessage={errors.applicationId || undefined}
            grow
          />
        </FormRow>
      </Stack>
    </Stack>
  );
};

export default ExtensionDetailsForm;
